import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Award, Loader2, RefreshCw, Search, Send, Eye } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDeShort } from "@/lib/date";
import { CertificatePreviewDialog } from "@/components/CertificatePreviewDialog";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
const FILTERS = [["missing", "Ohne Zertifikat"], ["unsent", "Erzeugt, nicht gesendet"], ["sent", "Gesendet"], ["all", "Alle"]];

function Status({ r }) {
  if (!r.count) return <span className="text-[11px] px-2 py-0.5 bg-amber-50 text-amber-800 border border-amber-200">fehlt</span>;
  if (!r.emailed_at) return <span className="text-[11px] px-2 py-0.5 bg-sky-50 text-sky-800 border border-sky-200">{r.count} erzeugt · nicht gesendet</span>;
  return <span className="text-[11px] px-2 py-0.5 bg-emerald-50 text-emerald-800 border border-emerald-200">{r.count} gesendet {formatDeShort(r.emailed_at.slice(0, 10))}</span>;
}

export default function ZertifikatePage() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("missing");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  const load = () => api.get("/certificates/overview").then((r) => setRows(r.data)).catch(err);
  useEffect(() => { load(); }, []);

  const shown = useMemo(() => (rows || []).filter((r) => (filter === "all" || (filter === "missing" ? !r.count : filter === "unsent" ? r.count && !r.emailed_at : r.emailed_at))
    && (!q.trim() || `${r.name} ${r.class_name || ""}`.toLowerCase().includes(q.trim().toLowerCase()))), [rows, filter, q]);
  const count = (k) => (rows || []).filter((r) => (k === "all" || (k === "missing" ? !r.count : k === "unsent" ? r.count && !r.emailed_at : r.emailed_at))).length;

  const generate = async (r, openPreview = true) => {
    setBusy(r.id);
    try {
      await api.post(`/clients/${r.id}/certificates/regenerate`, null, { params: { send: false } });
      if (openPreview) setPreview(r);
      return true;
    } catch (e) { if (openPreview) err(e); return false; } finally { setBusy(""); load(); }
  };
  const generateAll = async () => {
    const todo = (rows || []).filter((r) => !r.count);
    if (!todo.length || !window.confirm(`${todo.length} Zertifikat-Sätze erzeugen? Es wird noch NICHTS gesendet – du prüfst danach jeden in der Vorschau.`)) return;
    let ok = 0;
    for (const r of todo) { if (await generate(r, false)) ok += 1; }
    toast.success(`${ok} von ${todo.length} erzeugt${ok < todo.length ? " – bei den übrigen fehlen Module/Vertrag in der Akte" : ""}`);
    setFilter("unsent");
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10 space-y-8" data-testid="zertifikate-page">
      <div>
        <div className="text-[11px] tracking-[0.2em] uppercase text-slate-500 font-semibold">AVGS · Weiterbildung</div>
        <h1 className="text-4xl sm:text-5xl tracking-tight text-slate-900 mt-2">Zertifikate</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">Alle abgeschlossenen Kunden und Teilnehmer. Erzeugen → Vorschau prüfen → per E-Mail senden. Kunden sehen ihr Zertifikat im Portal erst nach dem Senden.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} className={`text-xs px-3 py-1.5 border transition-colors ${filter === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 hover:border-slate-900"}`} data-testid={`zert-filter-${k}`}>{l} · {count(k)}</button>
        ))}
        <div className="flex items-center gap-2 border border-slate-300 bg-white px-3 py-1.5 ml-auto">
          <Search size={13} className="text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name suchen …" className="text-xs outline-none bg-transparent w-40" data-testid="zert-search" />
        </div>
        <button onClick={generateAll} disabled={!!busy || !count("missing")} className="text-xs px-3 py-1.5 bg-[#00807E] text-white hover:bg-[#006a68] disabled:opacity-40 inline-flex items-center gap-1.5" data-testid="zert-generate-all"><Award size={13} /> Alle fehlenden erzeugen</button>
      </div>
      <div className="border border-slate-200 bg-white divide-y divide-slate-100" data-testid="zert-list">
        {!rows ? <div className="p-8 text-sm text-slate-500">Lädt …</div> : shown.length === 0 ? <div className="p-8 text-sm text-slate-500" data-testid="zert-empty">Keine Einträge.</div> : shown.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-3 px-5 py-3 hover:bg-slate-50 transition-colors" data-testid={`zert-row-${r.id}`}>
            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 ${r.kind === "wb" ? "bg-sky-100 text-sky-800" : "bg-teal-100 text-teal-800"}`}>{r.kind === "wb" ? "WB" : "AVGS"}</span>
            <Link to={r.link} className="text-sm font-medium text-slate-900 hover:underline min-w-[180px]">{r.name}</Link>
            <span className="text-xs text-slate-500">{r.class_name ? `${r.class_name} · ` : ""}{formatDeShort(r.start)} – {formatDeShort(r.end)}</span>
            <Status r={r} />
            <div className="ml-auto flex gap-2">
              <button onClick={() => generate(r)} disabled={!!busy} className="text-[11px] border border-slate-300 px-2.5 py-1 hover:border-slate-900 disabled:opacity-40 inline-flex items-center gap-1" data-testid={`zert-generate-${r.id}`}>
                {busy === r.id ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} {r.count ? "Neu erzeugen" : "Erzeugen"}
              </button>
              {r.count > 0 && (
                <button onClick={() => setPreview(r)} className="text-[11px] bg-slate-900 text-white px-2.5 py-1 hover:bg-black inline-flex items-center gap-1" data-testid={`zert-preview-${r.id}`}>
                  {r.emailed_at ? <Eye size={11} /> : <Send size={11} />} Vorschau{r.emailed_at ? "" : " & senden"}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {preview && <CertificatePreviewDialog clientId={preview.id} name={preview.name} open={!!preview} onClose={() => setPreview(null)} onSent={load} />}
    </div>
  );
}
